// Brand + domain configuration — the single source of truth for the
// product name and every URL / email address derived from the domain.
//
// The domain is pomela.app, and nothing below hard-codes it: set BRAND_DOMAIN
// (workers + Node/CI) and VITE_BRAND_DOMAIN (creator build). Until then
// the defaults use a non-routable `.example` placeholder so a
// misconfigured deploy fails loud instead of pointing at a stranger's
// website. See the README's "Before launch" section. One env-var edit
// re-points the whole product.

// Read an env var. `process` is absent in a plain browser bundle — the
// ReferenceError is caught. Literal `process.env.X` accesses (inside the
// thunks below) let Vite's `define` replace them at the creator build.
// process is provided by nodejs_compat (Workers) and by Node (CI); a plain
// browser bundle lacks it and the try/catch below absorbs the ReferenceError.
// Declared module-locally so this file type-checks in every package context
// without each consumer needing @types/node.
declare const process: { env: Record<string, string | undefined> };

function fromEnv(read: () => string | undefined): string | undefined {
  try {
    const v = read();
    return v && v.length > 0 ? v : undefined;
  } catch {
    return undefined;
  }
}

const DOMAIN = fromEnv(() => process.env.BRAND_DOMAIN) ?? 'TODO-CHOOSE-DOMAIN.example';

export const BRAND = {
  /** Product name. Safe to keep — the founder owns the name, not the domain. */
  name: fromEnv(() => process.env.BRAND_NAME) ?? 'pomela',
  /** Apex domain the founder owns. Everything below derives from it. */
  domainApex: DOMAIN,
  /** Where the creator app is served. */
  creatorOrigin: fromEnv(() => process.env.CREATOR_ORIGIN) ?? `https://app.${DOMAIN}`,
  /** Where generated apps are served. */
  appsOrigin: fromEnv(() => process.env.APPS_ORIGIN) ?? `https://${DOMAIN}`,
  /**
   * API gateway origin. Default is the EMPTY STRING — the runtime SDK
   * concatenates `${apiOrigin}/v1/<path>` so an empty base yields a
   * relative path that the browser resolves against the current page's
   * origin. This is the right shape for generated apps which:
   *   • run at https://<appId>.<domain>/ (custom domain) OR
   *   • run at https://<hash>.<project>.pages.dev/preview/<appId>/
   * The Cloudflare Pages Function at functions/v1/[[path]].ts relays
   * `/v1/*` to the api-gateway server-side, so cookies stay first-party
   * and there's no CORS hop. Explicit override only when a deploy must
   * skip the Pages relay (e.g. a fully server-side ingester pointed at
   * the workers.dev URL).
   */
  apiOrigin: fromEnv(() => process.env.API_ORIGIN) ?? '',
  /**
   * The SEPARATE registrable domain generated apps are served from, one
   * subdomain per app (`{appId}.pomela.page`). This is deliberately NOT
   * `appsOrigin`: the apex-isolation migration moved apps off the creator's
   * domain so a malicious app cannot receive the creator's cookies, and the
   * app-server matches `{appId}.pomela.page` by Host.
   *
   * Anything a CRAWLER or a link unfurler will read — canonical, og:url,
   * sitemap `<loc>`, the robots `Sitemap:` line — must be built from this,
   * never from the request origin. A build can run on an internal hostname
   * (`gen.internal` during a cache-miss rehydrate), and publishing that to
   * a sitemap points every crawler at a host that does not resolve.
   */
  appPageOrigin: fromEnv(() => process.env.APP_PAGE_ORIGIN) ?? 'https://pomela.page',
  /** Public-facing support address. */
  supportEmail: fromEnv(() => process.env.SUPPORT_EMAIL) ?? `hello@${DOMAIN}`,
  /** Transactional "from" address (magic-link + spend-cap alert emails). */
  fromEmail: fromEnv(() => process.env.FROM_EMAIL) ?? `noreply@${DOMAIN}`
} as const;

/**
 * The PUBLIC origin a generated app is reachable at — the only correct basis for
 * canonical/og:url/sitemap/robots. Falls back to the apex path form when the app id
 * is not a valid DNS label, which is the same fallback the app-server serves.
 */
export function appPublicOrigin(appId: string): string {
  const base = BRAND.appPageOrigin.replace(/\/+$/, '');
  return /^[a-z0-9][a-z0-9-]*$/i.test(appId)
    ? base.replace('://', `://${appId.toLowerCase()}.`)
    : `${base}/${encodeURIComponent(appId)}`;
}

/** True while the domain is still the placeholder — callers can fail loud. */
export const BRAND_DOMAIN_UNSET = DOMAIN.includes('TODO-CHOOSE-DOMAIN');
